/// <amd-module name="TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Components/AboutProject/AboutProjectCard" />
import * as React from "react";

import { format } from "VSS/Core/Util/String";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";

import { LegacyComponent } from "VSS/Legacy/Legacy";
import { RegionComponent, WrappedComponent } from "VSS/Platform/Layout";

import { ContributedAboutRegion } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Components/AboutProject/PanelAboutRegion/ContributedAboutRegion";
import { ComponentRegionNames } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Constants";
import { SourceRepositoryTypes, TagInfo } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Models";
import * as Resources from "TFS/Views/ProjectOverviewPage/Resources";

export interface AboutProjectCardProps {
    onEditProject: () => void;
    description: string;
    projectId: string;
    showLikes: boolean;
    tags: string[];
    showEditOption: boolean;
    projectName: string;
    repoId: string | undefined;
    repoType: SourceRepositoryTypes | undefined;
    onMarkTTI: (source: string) => void;
    aboutProjectTitleId: string;
}

export const AboutProjectCard = (props: AboutProjectCardProps) => {
    const tags: TagInfo[] = (props.tags || []).map(tagName => {
        return {
            text: tagName,
            tagItemAriaLabel: format(Resources.Tag_ItemNameAriaLabel, tagName),
            deleteButtonAriaLabel: format(Resources.Tag_DeleteButtonAriaLabel, tagName),
            isFocusable: false
        };
    });

    const { description, onEditProject, onMarkTTI, projectId, projectName, repoId, repoType, showEditOption, showLikes, aboutProjectTitleId } = props;

    return (
        <Card className={"project-overview-card"}> 
            <div className="about-project-card project-overview-card-container" role="region" aria-labelledby={aboutProjectTitleId}>
                <div className="title-section flex">
                    <div className="title-m" role="heading" aria-level={2} id={aboutProjectTitleId}>
                        {Resources.AboutProjectTitle}
                    </div>
                    <div className="title-right-content flex">
                        {showLikes && (
                            <div className="like-component">
                                <WrappedComponent
                                    dependencies={["ms.vss-social.vss-social-likes"]}
                                    wrappedType={"statefulLikeHeart"}
                                    wrappedProps={{
                                        className: "project-like-component",
                                        ariaLabelLiked: Resources.LikeButton_Liked,
                                        ariaLabelUnliked: Resources.LikeButton_Unliked,
                                        likeArtifactInfo: {
                                            artifactId: projectId,
                                            artifactType: "Project",
                                            artifactScopeType: "Project",
                                            artifactScopeId: projectId
                                        }

                                    }}
                                />
                            </div>
                        )}
                        {showEditOption && (
                            <Button
                                subtle={true}
                                className={"project-description-edit-button"}
                                tooltipProps={{ text: Resources.AboutProjectEditButtonAriaLabel }}
                                iconProps={{ iconName: "Edit" }}
                                onClick={onEditProject}
                            />
                        )}
                    </div>
                </div>
                {description && <div className="description about-project-description font-size-m">{description}</div>}
                <div className="tags-component">
                    <RegionComponent
                        className="languages-section"
                        componentRegion={ComponentRegionNames.ProjectOverviewLanguagesRegion}
                        childComponentProperties={{ showHeader: true }}
                    />
                    {tags.length > 0 && (
                        <div className="tags-section">
                            <div className="title title-xs">{Resources.AboutProjectPanelTagsHeading}</div>
                            <LegacyComponent
                                wrappedType="TFS.ProjectOverview.TagListControl"
                                modules={["ProjectOverview/Scripts/Shared/Components/TagListControl"]}
                                tags={tags}
                                editMode={false}
                                listAriaLabel={Resources.ProjectTags_AriaLabel}
                            />
                        </div>
                    )}
                </div>
                <div className="about-section">
                    <ContributedAboutRegion
                        projectName={projectName}
                        readMode={true}
                        showError={showEditOption}
                        selectedRepoId={repoId}
                        selectedRepoType={repoType}
                        onMarkTTI={onMarkTTI}
                    />
                </div>
            </div>
        </Card>
    );
};
